import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import { works } from "@/mocks/works";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function WorkDetail() {
  const { id } = useParams();
  const work = works.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();

  usePageMeta(
    work ? `影视作品：《${work.title}》详情 - 濑户环奈官网` : "作品未找到 - 濑户环奈官网",
    work
      ? `濑户环奈影视作品《${work.title}》详情：${work.summary}`
      : "未找到对应的影视作品。",
  );

  if (!work) {
    return (
      <PageShell>
        <div className="w-full px-4 md:px-8 py-24">
          <div className="max-w-3xl mx-auto flex flex-col items-center text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-3xl mb-5">
              <i className="ri-film-line"></i>
            </span>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
              未找到该影视作品
            </h1>
            <p className="text-sm text-foreground-600 mb-6">该作品可能已下架或链接有误。</p>
            <Link
              to="/works"
              className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              浏览全部作品
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  const related = works
    .filter((item) => item.id !== work.id && (item.type === work.type || item.year === work.year))
    .sort((a, b) => b.year - a.year)
    .slice(0, 3);

  const favorited = isFavorite(work.id);

  return (
    <PageShell>
      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <div className="max-w-6xl mx-auto">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "影视作品", to: "/works" },
              { label: `《${work.title}》` },
            ]}
          />
        </div>
      </div>

      <div className="w-full px-4 md:px-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          <div className="w-full">
            <div className="w-full aspect-[3/4] rounded-lg overflow-hidden bg-background-200 border border-background-200">
              <img
                src={work.cover}
                alt={`影视作品《${work.title}》海报`}
                title={`${work.title} - 濑户环奈影视作品`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                {work.type}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">
                {work.year}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800">
                {work.duration}
              </span>
            </div>

            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              《{work.title}》
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
              {work.summary}
            </p>

            <div className="flex flex-col gap-3 mb-6 text-sm">
              <div className="flex gap-3">
                <span className="text-foreground-400 w-16 shrink-0">导演</span>
                <span className="text-foreground-800">{work.director}</span>
              </div>
              <div className="flex gap-3">
                <span className="text-foreground-400 w-16 shrink-0">主要演员</span>
                <span className="text-foreground-800">{work.cast.join("、")}</span>
              </div>
              <div className="flex gap-3">
                <span className="text-foreground-400 w-16 shrink-0">年代</span>
                <span className="text-foreground-800">{work.year} 年</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {work.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-background-200 text-foreground-600"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => toggleFavorite(work.id)}
                aria-pressed={favorited}
                className={`inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  favorited
                    ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                    : "border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                }`}
              >
                <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
                {favorited ? "已收藏" : "收藏作品"}
              </button>
              <Link
                to="/works"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回列表
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-6xl mx-auto">
          <section className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
              作品介绍
            </h2>
            <div className="flex flex-col gap-4 max-w-3xl">
              {work.description.map((paragraph, index) => (
                <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </section>

          {work.gallery.length > 0 && (
            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                剧照
              </h2>
              <div className="w-full rounded-lg overflow-hidden bg-background-200 border border-background-200">
                <img
                  src={work.gallery[0]}
                  alt={`《${work.title}》剧照`}
                  title={`${work.title} - 剧照`}
                  className="w-full h-auto object-cover"
                />
              </div>
            </section>
          )}

          {related.length > 0 && (
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {related.map((item) => (
                  <WorkCard key={item.id} work={item} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </PageShell>
  );
}